/**
 * First-touch attribution payload — server-side reader + allowlist.
 *
 * The cookie name is `__feelgood_attribution`. The client-side capture hook
 * (`src/lib/marketing/capture-attribution.ts`) writes this cookie on first
 * mount of any landing page. The server reads it at:
 *
 *   - /api/intake/start: parsed and persisted to users.acquisition (jsonb,
 *     migration 0027). Idempotent — only writes on the first signUpEmail call
 *     (subsequent reused-session calls short-circuit before this).
 *
 *   - A2/A3 (future PRs): event-firing time. FB CAPI uses `fbclid` for the
 *     `fbc` user_data field; Everflow uses `_ef_transaction_id` as the
 *     postback `transaction_id`.
 *
 * Allowlist defense
 *
 * The cookie value is set client-side via `document.cookie`, which means a
 * malicious or buggy client could try to write any key. The server's
 * `sanitizeAttribution` runs every cookie payload through this allowlist:
 * any key not on the list is dropped silently. This prevents leaks of
 * user_id, session_id, email, auth tokens, or any field that isn't pure
 * marketing attribution.
 *
 * Postgres can't enforce jsonb key sets at the DB layer; this module IS
 * that boundary. Treat any read of `users.acquisition` as already-sanitized
 * (pre-A1 rows backfilled to `'{}'` so they pass too).
 */

import type { NextRequest } from 'next/server';

export const COOKIE_NAME = '__feelgood_attribution';

/** Marketing-event dedup cookie — separate from __feelgood_attribution
 *  so the allowlist boundary in `sanitizeAttribution` stays pure (no
 *  user-supplied session_id can land in the attribution payload). One
 *  uuid per browser, set at first-touch alongside the attribution
 *  cookie, lifetime mirrors. Read by `findOrCreateMarketingEventLog` as
 *  the primary dedup key (with ip_ua_hash + day_bucket as fallback for
 *  ITP / cookie eviction). */
export const MARKETING_SESSION_COOKIE_NAME = '__feelgood_marketing_session';

/** 60 days, matching prep doc Q3 + decision 7. Industry standard. */
export const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 60;

/** Domain attribute spans dev / admin / dev2 / bare subdomains. */
export const COOKIE_DOMAIN = '.rxhealthtoday.com';

/** Cap per-value length so a malicious cookie can't bloat users.acquisition. */
const MAX_VALUE_LENGTH = 512;

/** Allowlist for cookie payload keys. Any other key is dropped silently
 *  by `sanitizeAttribution`. Adding a key here requires (a) updating the
 *  client capture hook to populate it, (b) adding a corresponding read in
 *  the consumer (FB CAPI / Everflow adapter), (c) per the 2026-04-28
 *  LESSON, updating any *parallel* hand-maintained predicate that gates
 *  on a subset (e.g. the `hasClickId`/`hasNetwork` checks in
 *  `capture-attribution.ts` + `middleware.ts`). The drift-guard test in
 *  `scripts/test-cookie-network-id-expansion.ts` (and the prior A4 test)
 *  source-greps the predicate gates against the allowlist. */
export const ALLOWED_ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'fbclid',           // Meta click ID → FB CAPI user_data.fbc
  'gclid',            // Google Ads click ID → future Google CAPI
  'ttclid',           // TikTok Ads click ID → future TikTok Events API (A4)
  'msclkid',          // Microsoft (Bing) Ads click ID → future MS UET (A4)
  '_ef_transaction_id', // Everflow primary click ID
  'clickid',          // Everflow alias (some link templates use this)
  'affid',            // Affiliate ID — primary network identifier (the value the
                      // network knows us by). Operator-built tracking URLs put
                      // this in `affid=` per the Everflow/most-network convention.
  'pub',              // Sub-affiliate identifier (publisher) — most common name
                      // network-side for "the sub-aff inside this network".
                      // Phase 4 inbound-passthrough: first non-empty of
                      // pub > sub1 > affid4 > affid is the affiliate_clicks.aff_id.
  'sub1',             // Sub-affiliate identifier alias (Everflow + many partner networks)
  'sub2',             // Free-form per-link tracking slot (campaign / creative / placement)
  'sub3',             // Free-form per-link tracking slot
  'sub4',             // Free-form per-link tracking slot
  'sub5',             // Free-form per-link tracking slot
  'oid',              // Offer ID — which offer the network sent traffic for.
                      // Free-form text in v1 (no funnels table; offer_id stays
                      // a label, not an FK).
  'oid2',             // Secondary offer slot (some networks send a stack)
  'oid3',
  'oid4',
  'uid',              // Network-side user/visit identifier (some networks pass
                      // their per-visitor ID via uid alongside the click ID).
  'affid2',           // Secondary affiliate identifier slot
  'affid3',
  'affid4',           // Quaternary slot — sometimes used as a secondary sub-aff
                      // ID by operators with multi-tier networks. Last fallback
                      // in the aff_id precedence chain.
  'network_id',       // Affiliate network slug (e.g. 'bridge-ads'). Operator-
                      // constructed tracking URLs MAY embed this; when absent,
                      // network resolution falls back to looking up the
                      // inbound `affid` value against networks.link_defaults
                      // (PR #95 Link Builder column) — see
                      // affiliate-click-ingestion.resolveNetworkByLinkDefault.
  'referrer',         // document.referrer at first touch
  'landing_path',     // window.location.pathname at first touch
  'captured_at',      // ISO 8601 timestamp at first touch
] as const;

export type AttributionKey = typeof ALLOWED_ATTRIBUTION_KEYS[number];
export type AttributionPayload = Partial<Record<AttributionKey, string>>;

/**
 * Filter an arbitrary object to only allowlisted keys with safe string
 * values. Returns `{}` for anything non-object-shaped or empty. Length-caps
 * each value at 512 chars.
 *
 * Drops silently — no throw, no log. The cookie is best-effort attribution;
 * an attacker writing garbage shouldn't 500 our intake.
 */
export function sanitizeAttribution(input: unknown): AttributionPayload {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const src = input as Record<string, unknown>;
  const out: AttributionPayload = {};
  for (const key of ALLOWED_ATTRIBUTION_KEYS) {
    const v = src[key];
    if (typeof v === 'string' && v.length > 0 && v.length <= MAX_VALUE_LENGTH) {
      out[key] = v;
    }
  }
  return out;
}

/**
 * Read + parse the __feelgood_attribution cookie from a NextRequest.
 * Returns the sanitized payload, or `{}` if missing/malformed/non-JSON.
 */
export function parseAttributionCookie(req: NextRequest): AttributionPayload {
  const raw = req.cookies.get(COOKIE_NAME)?.value;
  if (!raw) return {};
  try {
    const decoded = decodeURIComponent(raw);
    const parsed: unknown = JSON.parse(decoded);
    return sanitizeAttribution(parsed);
  } catch {
    return {};
  }
}

/** True when the payload has at least one attribution-bearing key. Useful
 *  for skipping the DB write entirely when nothing was captured. */
export function hasAttribution(p: AttributionPayload): boolean {
  return Object.keys(p).length > 0;
}
